import React from 'react';

import { useQuery, UseQueryResult } from '@lumapps/base-api/react-query';
import { get as getConfig } from '@lumapps/constants';
import { AvatarProps } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { get, userDirectoryQueryKeys } from '@lumapps/user-directory/api';
import {
    useSocialProfileRoute,
    UseSocialProfileRouteResponse,
} from '@lumapps/user-profile/hooks/useSocialProfileRoute';

import { MinimumViableUser, UseUserOptions } from '../types';
import { getUserFullName } from '../utils/getUserFullName';
import { useUserAvatar } from './useUserAvatar';
import { useUserFields, DEFAULT_FIELDS } from './useUserFields';

export type UseUserResult<T extends MinimumViableUser = MinimumViableUser> = UseQueryResult<T> & {
    /** retrieve the user's full name */
    getUserFullName: () => string;
    /** retrieve the user's profile picture */
    getUserProfilePicture: () => string;
    /** calculated user fields */
    userFields?: (string | React.ReactNode)[];
    /** properties for the provided user's avatar */
    avatarProps: AvatarProps;
    /** props related to displaying a link towards the user's profile */
    userProfile: UseSocialProfileRouteResponse;
    /** user minimum viable user */
    user?: T;
};

const { userLang } = getConfig();

/**
 * Hooks that retrieves information from a given user, as well as enhances it with other calculated informations:
 * - User fields dependening on the organization / api profile fields
 * - Retrieve information in order to create a user's avatar.
 * - Retrieve information in order to create a link towards the user's profile.
 * - Retrieve the user's full name
 * - Retrieve the user's profile picture
 * @returns useUser
 */
export const useUser = <T extends MinimumViableUser = MinimumViableUser>(
    user?: T,
    options?: UseUserOptions,
): UseUserResult<T> => {
    const { translateKey } = useTranslate();
    const userProfile = useSocialProfileRoute({
        ...options,
        routeParams: { ...options?.routeParams, userId: user?.id },
    });

    const query = useQuery<T>({
        enabled: false,
        ...options,
        placeholderData: user,
        queryKey: userDirectoryQueryKeys.get({ uid: user?.id, email: user?.email, ...user }),
        queryFn: async () => {
            const { data } = await get({ uid: user?.id, email: user?.email });

            return {
                ...user,
                ...data,
            };
        },
    });

    const finalUser = query.data || user;

    const userFields = useUserFields(finalUser, options);
    const { getUserProfilePicture, ...userAvatar } = useUserAvatar(finalUser, options);

    return React.useMemo(() => {
        return {
            ...query,
            user: finalUser,
            // eslint-disable-next-line deprecation/deprecation
            getUserFullName: () => getUserFullName(finalUser, userLang, translateKey(GLOBAL.UNNAMED)),
            getUserProfilePicture,
            avatarProps: userAvatar,
            userFields,
            userProfile,
        };
    }, [finalUser, getUserProfilePicture, query, translateKey, userAvatar, userFields, userProfile]);
};

export { DEFAULT_FIELDS };
